import type { Coords, Cuisine, District, Tag, VenueDetail, VenueListItem } from "./types";

/** One shared password for two phones (see backend require_key). Stored locally
 *  so the phones only ask once. Never a real account system. */
const KEY_STORAGE = "cigar.appkey";

export function getKey(): string {
  return localStorage.getItem(KEY_STORAGE) ?? "";
}
export function setKey(v: string) {
  localStorage.setItem(KEY_STORAGE, v);
}
export function clearKey() {
  localStorage.removeItem(KEY_STORAGE);
}

export class Unauthorized extends Error {}

async function req<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(path, {
    ...init,
    headers: {
      "X-App-Key": getKey(),
      ...(init?.body ? { "Content-Type": "application/json" } : {}),
      ...(init?.headers ?? {}),
    },
  });
  if (res.status === 401) throw new Unauthorized("密碼唔啱");
  if (!res.ok) {
    let detail = `HTTP ${res.status}`;
    try {
      const j = await res.json();
      if (j?.error) detail = j.error;
      else if (Array.isArray(j?.detail)) detail = j.detail[0]?.msg ?? detail;
      else if (typeof j?.detail === "string") detail = j.detail;
    } catch {
      /* keep the status text */
    }
    throw new Error(detail);
  }
  return res.json() as Promise<T>;
}

/** 備註類別係封閉清單，一個 session 之內唔變 —— 三個畫面共用一次請求。 */
let _tagsCache: Promise<Tag[]> | null = null;

export const api = {
  /** district 令個數跟住地區走 —— 唔係嘅話下拉會寫「港式（67 間）」但實際 0 間。 */
  cuisines: (district?: string) =>
    req<Cuisine[]>(`/api/cuisines${district ? `?district=${encodeURIComponent(district)}` : ""}`),

  /** 區份 + 中心點。冇法定位時（桌面機冇 GPS／WiFi 定位）用嚟手動揀。 */
  districts: () => req<District[]>("/api/districts"),

  /** 備註類別 + 有幾多間 tag 過。

   *  會回**全部**標籤（未用過 count = 0）：記錄到訪嗰陣要全部揀得到，
   *  篩選嗰陣先由 UI 收埋 0 間嗰啲。
   *
   *  唔帶 district 嗰陣做 session 級 cache —— 呢個係封閉清單，一個 session
   *  之內唔會變，但 Home／LogSheet／Detail 三個畫面都要攞。 */
  tags: (district?: string) => {
    if (district) return req<Tag[]>(`/api/tags?district=${encodeURIComponent(district)}`);
    _tagsCache ??= req<Tag[]>("/api/tags").catch((e) => {
      _tagsCache = null;      // 失敗唔可以 cache 住，否則成個 session 都攞唔到
      throw e;
    });
    return _tagsCache;
  },

  venues: (p: {
    cuisine?: string;
    district?: string;
    coords?: Coords;
    openNow?: boolean;
    q?: string;
    /** 我自己落嘅到訪篩選 —— 全部要落喺同一次到訪（後端負責） */
    tag?: string;
    verdict?: string;
    area?: string;
    who?: string;
    visited?: "" | "yes" | "no";
    limit?: number;
  }) => {
    const qs = new URLSearchParams();
    if (p.cuisine) qs.set("cuisine", p.cuisine);
    if (p.district) qs.set("district", p.district);
    if (p.coords) {
      qs.set("lat", String(p.coords.lat));
      qs.set("lng", String(p.coords.lng));
    }
    if (p.openNow) qs.set("open_now", "true");
    if (p.q) qs.set("q", p.q);
    if (p.tag) qs.set("tag", p.tag);
    if (p.verdict) qs.set("verdict", p.verdict);
    if (p.area) qs.set("area", p.area);
    if (p.who) qs.set("who", p.who);
    if (p.visited) qs.set("visited", p.visited);
    if (p.limit) qs.set("limit", String(p.limit));
    return req<VenueListItem[]>(`/api/venues?${qs}`);
  },

  venue: (id: number, coords: Coords) => {
    const qs = new URLSearchParams();
    if (coords) {
      qs.set("lat", String(coords.lat));
      qs.set("lng", String(coords.lng));
    }
    return req<VenueDetail>(`/api/venues/${id}?${qs}`);
  },

  addVisit: (
    venueId: number,
    body: {
      cigar_confirmed: string;
      area_type?: string | null;
      who?: string;
      remark?: string | null;
      tags?: string[];
      visited_on?: string;
    },
  ) =>
    req<{ id: number }>(`/api/venues/${venueId}/visits`, {
      method: "POST",
      body: JSON.stringify(body),
    }),

  uploadPhoto: async (visitId: number, file: File) => {
    const fd = new FormData();
    fd.append("file", file);
    const res = await fetch(`/api/visits/${visitId}/photos`, {
      method: "POST",
      headers: { "X-App-Key": getKey() },
      body: fd,
    });
    if (!res.ok) throw new Error(`上載失敗 (${res.status})`);
    return res.json() as Promise<{ id: number; path: string }>;
  },
};

/** 距離顯示：<1km 用米，否則用公里一位小數。 */
export function fmtDistance(m: number | null | undefined): string {
  if (m == null) return "";
  return m < 1000 ? `${m} 米` : `${(m / 1000).toFixed(1)} 公里`;
}

/** 露天位許可時間：偶爾會過午夜（end > 24:00），要講清楚係第二日。 */
export function fmtOsaEnd(v: { osa_end: string | null; osa_end_min: number | null }): string {
  if (!v.osa_end) return "—";
  return v.osa_end_min != null && v.osa_end_min > 1440
    ? `${v.osa_end}（翌日）`
    : v.osa_end;
}

/**
 * 露天位許可時段。有啲牌照原文係「唔同日唔同時間」（例如週日至四 16:00–23:00、
 * 週五六 14:00–23:00）。將佢壓成一個範圍係誤導，所以嗰啲要顯示原文。
 */
export function osaLabel(v: {
  osa_start: string | null;
  osa_end: string | null;
  osa_end_min: number | null;
  osa_multi: boolean;
  osa_hours_text: string | null;
  osa_hours_zh?: string | null;
}): string {
  // 多時段：牌照原文寫法各日唔同，壓成一個範圍係誤導，所以顯示原文。
  // 但原文係英文 —— 有中文版就用中文，冇就跌返原文。
  if (v.osa_multi) return v.osa_hours_zh ?? v.osa_hours_text ?? "—";
  if (!v.osa_start || !v.osa_end) return v.osa_hours_zh ?? v.osa_hours_text ?? "—";
  return `${v.osa_start} – ${fmtOsaEnd(v)}`;
}

/** Google Maps 只可以用超連結：唔用 API、唔儲存任何 Google 內容。 */
export function googleMapsUrl(name: string, address?: string | null): string {
  const q = [name, address].filter(Boolean).join(" ");
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(q)}`;
}

export const todayHKT = () =>
  new Date(Date.now() + 8 * 3600 * 1000).toISOString().slice(0, 10);
